import React from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar, faHeart, faShieldAlt, faUsers, faAward, faTools, faTree, faPaintBrush } from '@fortawesome/free-solid-svg-icons';
import Reveal from '../components/Reveal';
import Counter from '../components/Counter';
import PageHeader from '../components/PageHeader';
import { useLanguage } from '../context/LanguageContext';
import './About.css';

const About = () => {
  const { t } = useLanguage();

  const values = [
    { icon: faStar, title: t('about_val1_title'), description: t('about_val1_desc') },
    { icon: faHeart, title: t('about_val2_title'), description: t('about_val2_desc') },
    { icon: faShieldAlt, title: t('about_val3_title'), description: t('about_val3_desc') },
    { icon: faUsers, title: t('about_val4_title'), description: t('about_val4_desc') }
  ];

  const commitments = [
    { icon: faAward, title: t('cert1_title'), description: t('cert1_desc') },
    { icon: faShieldAlt, title: t('cert2_title'), description: t('cert2_desc') },
    { icon: faHeart, title: t('cert3_title'), description: t('cert3_desc') }
  ];

  const stats = [
    { value: '15+', label: t('stat_years') },
    { value: '500+', label: t('stat_projects') },
    { value: '100%', label: t('stat_clients_satisfied') },
    { value: '1', label: t('stat_owner') }
  ];

  return (
    <div className="about-page">
      <PageHeader
        image="/img/buffet-sculpte.webp"
        eyebrow={t('about_eyebrow')}
        title={t('about_page_title')}
        subtitle={t('about_page_subtitle')}
      />

      {/* Histoire */}
      <section className="section">
        <div className="container story">
          <Reveal as="div" animation="left" className="story-text">
            <span className="eyebrow">{t('about_story_title')}</span>
            <p className="story-lead">{t('about_full_story_p1')}</p>
            <p>{t('about_full_story_p2')}</p>
            <p>{t('about_full_story_p3')}</p>
          </Reveal>
          <Reveal as="figure" animation="right" delay={120} className="story-figure">
            <div className="img-frame story-img">
              <img src="/img/atelier/atelier-route-mbour.webp" alt={t('home_workshop_img_alt')} loading="lazy" />
            </div>
            <figcaption>{t('about_workshop_caption')}</figcaption>
          </Reveal>
        </div>
      </section>

      {/* Atelier */}
      <section className="section section-dark">
        <div className="container workshop">
          <Reveal as="div" className="section-head">
            <span className="eyebrow">{t('about_workshop_title')}</span>
            <h2>KM1 Route de Joal, Mbour</h2>
          </Reveal>
          <div className="workshop-features">
            {[
              { icon: faTools, label: t('about_feat_tools'), image: '/img/atelier/assemblage-bois-tiroir.webp' },
              { icon: faTree, label: t('about_feat_wood'), image: '/img/atelier/bois-brut-sechage.webp' },
              { icon: faPaintBrush, label: t('about_feat_finish'), image: '/img/atelier/poncage-finition-vernis.webp' }
            ].map((feature, index) => (
              <Reveal as="div" key={feature.label} delay={index * 100} className="workshop-feature">
                <div className="img-frame workshop-feature-img">
                  <img src={feature.image} alt="" loading="lazy" />
                </div>
                <p><FontAwesomeIcon icon={feature.icon} /> {feature.label}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Valeurs */}
      <section className="section">
        <div className="container">
          <Reveal as="div" className="section-head">
            <span className="eyebrow">{t('about_values_eyebrow')}</span>
            <h2>{t('about_values_title')}</h2>
          </Reveal>
          <div className="values-grid">
            {values.map((value, index) => (
              <Reveal as="div" key={value.title} delay={index * 90} className="value-card">
                <span className="value-icon"><FontAwesomeIcon icon={value.icon} /></span>
                <h3>{value.title}</h3>
                <p>{value.description}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Artisan + chiffres */}
      <section className="section section-alt">
        <div className="container artisan">
          <Reveal as="div" animation="left" className="artisan-card">
            <img src="/img/logo.webp" alt="" width="96" height="96" loading="lazy" />
            <div>
              <span className="eyebrow">{t('about_team_title')}</span>
              <h3>Aliou Sene Ngom</h3>
              <p className="artisan-role">{t('about_member_role')}</p>
              <p>{t('about_member_desc')}</p>
            </div>
          </Reveal>
          <div className="about-stats">
            {stats.map((stat, index) => (
              <Reveal as="div" key={stat.label} delay={index * 90} className="about-stat">
                <strong><Counter value={stat.value} /></strong>
                <span>{stat.label}</span>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Mission et vision */}
      <section className="section">
        <div className="container mission">
          <Reveal as="div" animation="left" className="mission-card">
            <h3>{t('about_mission_title')}</h3>
            <p>{t('about_mission_desc')}</p>
          </Reveal>
          <Reveal as="div" animation="right" delay={100} className="mission-card">
            <h3>{t('about_vision_title')}</h3>
            <p>{t('about_vision_desc')}</p>
          </Reveal>
        </div>
      </section>

      {/* Engagements */}
      <section className="section section-alt">
        <div className="container">
          <Reveal as="div" className="section-head center">
            <h2>{t('about_cert_title')}</h2>
          </Reveal>
          <div className="commitments">
            {commitments.map((item, index) => (
              <Reveal as="div" key={item.title} delay={index * 90} className="commitment">
                <FontAwesomeIcon icon={item.icon} />
                <h3>{item.title}</h3>
                <p>{item.description}</p>
              </Reveal>
            ))}
          </div>
          <div className="about-cta">
            <Link to="/contact" className="btn btn-primary">{t('nav_quote_btn')}</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
